// src/broadcast/utils/validator.ts

import { z } from 'zod'
import { MessageBroadcastPayloadSchema } from '../schemas/events/message.schema'
import type { BroadcastEventType } from '../types/base'
import type { MessageBroadcastPayload } from '../types/events/message'

/**
 * Validation result type
 */
export type ValidationResult<T> =
  | { success: true; data: T }
  | { success: false; error: z.ZodError }

/**
 * Validate a raw broadcast payload
 * @param payload - Raw payload from Supabase broadcast
 * @returns Validated and typed payload or error
 */
export function validateBroadcastPayload(
  payload: unknown
): ValidationResult<MessageBroadcastPayload> {
  const result = MessageBroadcastPayloadSchema.safeParse(payload)

  if (result.success) {
    return { success: true, data: result.data as MessageBroadcastPayload }
  }

  return { success: false, error: result.error }
}

/**
 * Extract event type from raw payload (without full validation)
 * Useful for routing before validation
 * @param payload - Raw payload
 * @returns Event type or null if invalid
 */
export function getPayloadType(payload: unknown): BroadcastEventType | null {
  if (
    typeof payload === 'object' &&
    payload !== null &&
    'metadata' in payload &&
    typeof (payload as any).metadata === 'object' &&
    'type' in (payload as any).metadata
  ) {
    const type = (payload as any).metadata.type
    if (['message', 'notification', 'matching', 'system', 'image_generation'].includes(type)) {
      return type as BroadcastEventType
    }
  }
  return null
}

/**
 * Validate message payload specifically
 * @param payload - Raw payload
 * @returns Validated MessageBroadcastPayload or null
 */
export function validateMessagePayload(
  payload: unknown
): MessageBroadcastPayload | null {
  const result = MessageBroadcastPayloadSchema.safeParse(payload)
  return result.success ? (result.data as MessageBroadcastPayload) : null
}
